'use client'
import type { CredentialResponse } from '@react-oauth/google'
import type {
  LoginFormValues,
} from './constants'
import type {
  PostLoginResponseSuccess,
} from '~/lib/services/api/user'
import { zodResolver } from '@hookform/resolvers/zod'
import { GoogleLogin } from '@react-oauth/google'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'

import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  setCookieAnonymousTokenServerAction,
  setCookieRefreshTokenServerAction,
  setCookieTokenServerAction,
} from '~/actions/set-cookie'
import { Link, useRouter } from '~/i18n/routing'
import { getQueryClient, TAG_TYPES } from '~/lib/config'
import { routes } from '~/lib/constants'
import useGoogleLogin from '~/lib/hooks/auth/use-google-login'
import useLogin from '~/lib/hooks/auth/use-login'
import useResend from '~/lib/hooks/auth/use-resend-code'
import { hasInternalSharedAccess } from '~/lib/permissions'
import {
  LOGIN_ERROR_CODE,
} from '~/lib/services/api/user'
import { sleep } from '~/lib/utils'
import { Button } from '~/modules/common/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from '~/modules/common/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/modules/common/components/ui/form'
import { Input } from '~/modules/common/components/ui/input'
import { Separator } from '~/modules/common/components/ui/separator'

import { toast } from '~/modules/common/components/ui/use-toast'
import { PasswordInput } from '../../common/components/password-input'
import { TypographyH1 } from '../../common/components/typography/typography'
import {
  getInitialDefaultValues,
  getLoginFormSchema,
} from './constants'

export function LoginForm() {
  const t = useTranslations('page.login')

  const router = useRouter()
  const queryClient = getQueryClient()
  const searchParams = useSearchParams()
  const invitationToken = searchParams.get('invitationToken') || undefined

  const { mutateAsync: traditionalLogin, isPending } = useLogin()
  const { mutateAsync: googleLogin } = useGoogleLogin()
  const { mutateAsync: resendVerificationCode } = useResend()
  const loginFormSchema = getLoginFormSchema({ t })
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: getInitialDefaultValues({}),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  useEffect(() => {
    if (invitationToken) {
      toast({ title: t('inviteToast'), duration: 60 * 60 * 1000 })
    }
  }, [invitationToken, t])

  const handleLogin = async (
    res: PostLoginResponseSuccess,
    invitationToken?: string,
  ) => {
    try {
      if (invitationToken) {
        const destination = `${routes.orders}/invite/?invitationToken=${invitationToken}`
        router.push(destination)
        return
      }

      await Promise.all([
        setCookieAnonymousTokenServerAction(''),
        setCookieTokenServerAction(res.data.accessToken),
        setCookieRefreshTokenServerAction(res.data.refreshToken),
      ])

      const nextRoute = hasInternalSharedAccess(res.data.permissions)
        ? routes.dashboard
        : routes.orders

      router.push(nextRoute)
    }
    catch (err) {
      const { error } = err as APIError
      if (error?.code !== 500) {
        toast({
          title: error.message || t('invalidEmailOrPassword'),
        })
      }
    }
  }

  const handleTraditionalLogin = async (data: LoginFormValues) => {
    try {
      const res = await traditionalLogin(data)
      if ('data' in res) {
        await handleLogin(res, invitationToken)
      }
    }
    catch (err) {
      console.log(err)
      const baseError = err as APIError
      if (baseError && 'error' in baseError) {
        const { error } = baseError
        if (error.code === LOGIN_ERROR_CODE) {
          queryClient.setQueryData([TAG_TYPES.AUTH], { email: data.email })
          await resendVerificationCode({ email: data.email })
          toast({ title: t('verificationSent') })
          await sleep(1000)
          router.push('/verify')
          return
        }
        if (error.code === 400) {
          toast({
            title: error.message || t('invalidEmailOrPassword'),
          })
        }
      }
    }
  }

  const handleGoogleLogin = async (credentialResponse: CredentialResponse) => {
    try {
      if (!credentialResponse.credential) {
        toast({ title: t('googleLoginMissingCredential') })
        return
      }
      const res = await googleLogin({
        credential: credentialResponse.credential,
      })
      if ('data' in res) {
        await handleLogin(res, invitationToken)
      }
    }
    catch (err) {
      const baseError = err as APIError
      if (baseError && 'error' in baseError) {
        const { error } = baseError
        toast({
          title: error.message || t('googleLoginFailed'),
        })
      }
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleTraditionalLogin)}
        className="space-y-8 max-w-[440px]"
      >
        <Card className="mx-auto w-[400px] h-auto">
          <CardHeader>
            <TypographyH1>{t('login')}</TypographyH1>
            <CardDescription>{t('description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('form.inputs.email.label')}</FormLabel>
                      <FormControl>
                        <Input
                          id="email"
                          placeholder={t('form.inputs.email.placeholder')}
                          type="email"
                          required
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid gap-2">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <PasswordInput
                      field={field}
                      label={t('form.inputs.password.label')}
                    />
                  )}
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={isPending}
                isLoading={isPending}
              >
                {t('login')}
              </Button>
            </div>
            <div className="my-6 flex items-center gap-3">
              <Separator className="flex-1" />
              <span className="text-xs uppercase text-muted-foreground">
                {t('or')}
              </span>
              <Separator className="flex-1" />
            </div>
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => toast({ title: t('googleLoginFailed') })}
                text="signin_with"
                size="large"
                width={320}
                locale="en"
                shape="rectangular"
                theme="outline"
              />
            </div>
            <div className="flex flex-col gap-2 text-sm items-center justify-center">
              <div className="mt-4 text-center ">
                {t('accountQuestion')}
                {' '}
                <Link
                  href={{
                    pathname: routes.register,
                    search: searchParams.toString(),
                  }}
                  className="underline"
                >
                  {t('signUp')}
                </Link>
              </div>
              <div>
                {t('forgotPassword')}
                {' '}
                <Link href={routes.forgot} className="underline">
                  {t('resetHere')}
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  )
}
